import { useMutation } from "@apollo/react-hooks";
import { ChoiceList, Modal, Stack } from "@shopify/polaris";
import React, { useCallback, useEffect, useReducer, useRef } from "react";
import { ASSIGN_AMAZON_IDEA } from "../../../graphql/mutations/amazon-idea";
import { arrInvalid, handleError, reducerFn } from "../../../helper";
import useTimeout from "../../../hooks/useTimeout";
import { useToastContext } from "../../shared/ToastContext";
import StoreSelect from "../../store/components/StoreSelect";
import AmazonIdeaSupporter from "../amazon-idea-supporter";
import { useAmazonIdeaCtx } from "../context";
import { getStoreIds } from "../helper";

export default function AmazonIdeaAssign({ open, onClose, ids, onCompleted, node }) {
    const { setNotify, toggleToast } = useToastContext();
    const { refetch } = useAmazonIdeaCtx();

    const [state, setState] = useReducer(reducerFn, {
        selected: ["supporter"],
        storeIDs: [],
    });

    const [setDelay] = useTimeout();
    const [assignAmazonIdea, { loading }] = useMutation(ASSIGN_AMAZON_IDEA, {
        onCompleted: () => {
            setNotify({
                msg: "Asssign amazon idea successfully",
                err: false,
            });

            onClose();
            onCompleted && onCompleted();
            setDelay(() => {
                refetch && refetch();
            }, 1500);
        },
        onError: (err) => {
            setNotify({ msg: handleError(err?.toString()), err: true });
        },
    });

    const supporterRef = useRef(null);
    const handleSubmit = useCallback(() => {
        if (arrInvalid(ids)) return;

        toggleToast && toggleToast(true);
        setNotify && setNotify({ msg: null, err: false });

        const input = { ids };
        if (isSelectType(state.selected, "supporter")) {
            if (supporterRef.current?.getSupporter) {
                const supporterID = supporterRef.current.getSupporter();

                if (!supporterID) return;

                input.supporterID = supporterID;
            }
        } else if (isSelectType(state.selected, "store")) {
            if (arrInvalid(state.storeIDs)) return;

            input.storeIDs = state.storeIDs;
        }

        assignAmazonIdea({
            variables: input,
        });
    }, [ids, state.selected, state.storeIDs]);

    const handleStateChange = useCallback((selected) => {
        setState({ selected });
    }, []);

    const handleStoreChange = useCallback((value) => {
        setState({ storeIDs: value });
    }, []);


    useEffect(() => {
        const storeIds = getStoreIds(node?.stores);
        if (storeIds.length > 0) {
            setState({ storeIDs: storeIds, selected: ["store"] });
        }
    }, [JSON.stringify(node?.stores)]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            sectioned
            title="Assign Amazon Idea"
            primaryAction={{
                content: "Submit",
                onAction: handleSubmit,
                loading: loading,
            }}
            secondaryActions={[{ content: "Cancel", onAction: onClose }]}
        >
            {open ? (
                <Stack vertical>
                    <ChoiceList
                        title="Choose Type"
                        selected={state.selected}
                        onChange={handleStateChange}
                        choices={[
                            { value: "supporter", label: "Supporter" },
                            { value: "store", label: "Store" },
                        ]}
                    />

                    {isSelectType(state.selected, "store") ? (
                        <StoreSelect
                            value={state.storeIDs}
                            filter={{ platform: "amazon", limit: 200 }}
                            onChange={handleStoreChange}
                            getValueImmediate
                            allowMultiple
                        />
                    ) : null}

                    {isSelectType(state.selected, "supporter") ? (
                        <AmazonIdeaSupporter ref={supporterRef} />
                    ) : null}
                </Stack>
            ) : null}
        </Modal>
    );
}

function isSelectType(selected, target) {
    return selected.includes(target);
}
